// SKU-mapping combobox for Account Product Directive lines (Feature #40, M2).
//
// Confirm-on-map: type to search the STAR Master Product master via the raw-JSON
// `directives#product_search` endpoint (fetch, debounced), pick a result to persist
// its `product_code` on the line. Mapping is optional — the field can stay empty.
//
// SSR-safe: all browser work (fetch, listeners) runs inside event handlers /
// effects, never at module or render scope.

import * as React from "react"
import { Check, Loader2, Search, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { StarProductResult } from "@/lib/accountDirectives"

const SEARCH_URL = "/admin/accounts/directives/product_search"

type Props = {
  /** Currently mapped STAR product_code (null/"" = unmapped). */
  value: string | null
  /** Called with the chosen product_code (or null to clear the mapping). */
  onChange: (productCode: string | null) => void
  /** Optional id for the input (label association). */
  id?: string
}

export function SkuMappingCombobox({ value, onChange, id }: Props) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [results, setResults] = React.useState<StarProductResult[]>([])
  const [loading, setLoading] = React.useState(false)
  // Remember the human label of the just-picked product so the chip reads nicely
  // (the persisted value is only the code; on edit we fall back to the code).
  const [pickedLabel, setPickedLabel] = React.useState<string | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  // Debounced search.
  React.useEffect(() => {
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    const ctrl = new AbortController()
    const handle = window.setTimeout(() => {
      fetch(`${SEARCH_URL}?q=${encodeURIComponent(term)}`, {
        headers: { Accept: "application/json" },
        signal: ctrl.signal,
      })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((data: { results: StarProductResult[] }) => {
          setResults(data.results ?? [])
          setLoading(false)
        })
        .catch((err) => {
          if (err.name !== "AbortError") setLoading(false)
        })
    }, 250)
    return () => {
      window.clearTimeout(handle)
      ctrl.abort()
    }
  }, [query])

  const pick = (product: StarProductResult) => {
    onChange(product.product_code)
    setPickedLabel(
      product.product_name ? `${product.product_code} — ${product.product_name}` : product.product_code,
    )
    setOpen(false)
    setQuery("")
    setResults([])
  }

  const clear = () => {
    onChange(null)
    setPickedLabel(null)
    setQuery("")
  }

  // Mapped state: show a chip with the resolved code/label + a clear button.
  if (value) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md border border-hairline bg-surface px-2.5 py-1.5 text-sm text-ink-body">
          <Check className="h-3.5 w-3.5 shrink-0 text-accent" />
          <span className="truncate">{pickedLabel ?? value}</span>
        </span>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink-body"
        >
          <X className="h-3.5 w-3.5" />
          Lepas
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
        <Input
          id={id}
          value={query}
          autoComplete="off"
          placeholder="Cari kode/nama/brand SKU…"
          className="pl-8"
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-muted" />
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-hairline bg-surface shadow-lg">
          {results.length === 0 && !loading ? (
            <div className="px-3 py-2 text-sm text-ink-muted">Tidak ada SKU cocok.</div>
          ) : (
            <ul>
              {results.map((product) => (
                <li key={product.product_code}>
                  <button
                    type="button"
                    onClick={() => pick(product)}
                    className={cn(
                      "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm",
                      "hover:bg-page focus:bg-page focus:outline-none",
                    )}
                  >
                    <span className="font-medium text-ink-body">
                      {product.product_code}
                      {product.product_name ? ` — ${product.product_name}` : ""}
                    </span>
                    {product.brand_name && (
                      <span className="text-xs text-ink-muted">{product.brand_name}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
